import * as React from "react";
import { Typography, Box } from "@mui/material";
import { ILatestReportsResponse, IDiffResponse } from "../../services/backend/GameConfigService";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";

export interface BranchStateReportProps {
  reports: ILatestReportsResponse | undefined;
  selectedReportView: "import" | "deploy" | "legacy" | "changes";
  diffData: IDiffResponse | undefined;
}

export const BranchStateReport: React.FC<BranchStateReportProps> = ({ reports, selectedReportView, diffData }) => {
  if (!reports) {
    return <Typography variant="body2">No reports available</Typography>;
  }

  switch (selectedReportView) {
    case "import":
      if (reports.importOutputLogFile.exists && reports.importOutputLogFile.content) {
        return (
          <pre
            style={{
              backgroundColor: "#f5f5f5",
              padding: 16,
              borderRadius: 4,
              maxHeight: "40vh",
              maxWidth: "70vw",
              overflow: "auto",
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word",
            }}
          >
            {reports.importOutputLogFile.content}
          </pre>
        );
      }
      return <Typography variant="body2">No import output available</Typography>;

    case "deploy":
      if (reports.deployOutputLogFile.exists && reports.deployOutputLogFile.content) {
        return (
          <pre
            style={{
              backgroundColor: "#f5f5f5",
              padding: 16,
              borderRadius: 4,
              maxHeight: "40vh",
              maxWidth: "70vw",
              overflow: "auto",
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word",
            }}
          >
            {reports.deployOutputLogFile.content}
          </pre>
        );
      }
      return <Typography variant="body2">No deploy output available</Typography>;

    case "legacy":
      if (reports.legacyImportFile.exists && reports.legacyImportFile.content) {
        return (
          <pre
            style={{
              backgroundColor: "#f5f5f5",
              padding: 16,
              borderRadius: 4,
              maxHeight: "40vh",
              maxWidth: "70vw",
              overflow: "auto",
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word",
            }}
          >
            {reports.legacyImportFile.content}
          </pre>
        );
      }
      return <Typography variant="body2">No legacy report available</Typography>;

    case "changes":
      if (!diffData || (!diffData.added.length && !diffData.removed.length && !diffData.updated.length)) {
        return <Typography variant="body2">No changes in branch</Typography>;
      }

      return (
        <div style={{ maxHeight: "40vh", overflow: "auto", paddingRight: 16 }}>
          {diffData.added.map((item, index) => (
            <Box key={`added-${index}`} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="success.main" sx={{ mb: 1 }}>
                Added: {item.path} ({item.id})
              </Typography>
              <ReactDiffViewer
                newValue={item.newContent || ""}
                rightTitle="New File"
                splitView={false}
                hideLineNumbers={false}
                showDiffOnly={false}
              />
            </Box>
          ))}
          {diffData.removed.map((item, index) => (
            <Box key={`removed-${index}`} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="error.main" sx={{ mb: 1 }}>
                Removed: {item.path} ({item.id})
              </Typography>
              <ReactDiffViewer
                oldValue={item.oldContent || ""}
                leftTitle="Deleted File"
                splitView={false}
                hideLineNumbers={false}
                showDiffOnly={false}
              />
            </Box>
          ))}
          {diffData.updated.map((item, index) => (
            <Box key={`updated-${index}`} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1 }}>
                Updated: {item.path} ({item.id})
              </Typography>
              <ReactDiffViewer
                oldValue={item.oldContent || ""}
                newValue={item.newContent || ""}
                leftTitle="Before"
                rightTitle="After"
                splitView={true}
                hideLineNumbers={false}
                showDiffOnly={true}
                compareMethod={DiffMethod.LINES}
              />
            </Box>
          ))}
        </div>
      );

    default:
      return <Typography variant="body2">Select a report type</Typography>;
  }
};
